import { Activity, Database, HardDrive, MonitorCheck, RefreshCw } from 'lucide-react'
import { DependencyCard, ServiceCard } from '@/components/staging/status-cards'
import { MetricCard } from '@/components/ui/metric-card'
import { useRefreshStaging, useStagingDeepHealth, useStagingHealth } from '@/hooks/use-staging'
import { formatUptime } from '@/lib/format'

const SERVICES = [
  { name: 'one-api', description: 'REST API', hasHealthEndpoint: true },
  { name: 'one-admin', description: 'Admin API', hasHealthEndpoint: false },
  { name: 'one-cron', description: 'Cron Jobs', hasHealthEndpoint: false },
  { name: 'one-dispatcher', description: 'Queue Consumer', hasHealthEndpoint: false },
  { name: 'one-ingest', description: 'Event Ingest', hasHealthEndpoint: false },
  { name: 'one-unsub', description: 'Unsubscribe Handler', hasHealthEndpoint: false },
] as const

function LoadingSkeleton(): React.JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
      {['api', 'admin', 'cron', 'dispatcher', 'ingest', 'unsub'].map((name) => (
        <div
          key={name}
          className="h-24 animate-pulse rounded-xl border border-surface-3 bg-surface-0"
        />
      ))}
    </div>
  )
}

export function StagingPage(): React.JSX.Element {
  const healthQuery = useStagingHealth()
  const deepHealthQuery = useStagingDeepHealth()
  const refresh = useRefreshStaging()

  const healthStatus = healthQuery.isError ? 'error' : healthQuery.data ? 'ok' : 'unknown'
  const lastChecked = healthQuery.dataUpdatedAt ? new Date(healthQuery.dataUpdatedAt) : null

  const pgCheck = deepHealthQuery.data?.checks.postgres
  const vkCheck = deepHealthQuery.data?.checks.valkey

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MonitorCheck className="h-6 w-6 text-admin-500" />
          <h1 className="text-2xl font-semibold text-text-primary">Staging Health</h1>
          {healthQuery.isFetching && (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-admin-500 border-t-transparent" />
          )}
        </div>
        <button
          type="button"
          onClick={refresh}
          className="flex items-center gap-2 rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-1"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Service Status Grid */}
      <section>
        <h2 className="mb-3 text-lg font-medium text-text-primary">Services</h2>
        {healthQuery.isLoading ? (
          <LoadingSkeleton />
        ) : (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            {SERVICES.map((svc) => (
              <ServiceCard
                key={svc.name}
                name={svc.name}
                description={svc.description}
                status={svc.hasHealthEndpoint ? healthStatus : 'unknown'}
                hasHealthEndpoint={svc.hasHealthEndpoint}
              />
            ))}
          </div>
        )}
      </section>

      {/* Deep Health -- Dependencies */}
      <section>
        <h2 className="mb-3 text-lg font-medium text-text-primary">Dependencies</h2>
        <div className="grid grid-cols-2 gap-4">
          <DependencyCard
            name="Postgres"
            icon={Database}
            status={pgCheck ? pgCheck.status : deepHealthQuery.isError ? 'error' : 'unknown'}
            latencyMs={pgCheck?.latency_ms ?? null}
          />
          <DependencyCard
            name="Valkey"
            icon={HardDrive}
            status={vkCheck ? vkCheck.status : deepHealthQuery.isError ? 'error' : 'unknown'}
            latencyMs={vkCheck?.latency_ms ?? null}
          />
        </div>
      </section>

      {/* System Metrics */}
      <section>
        <h2 className="mb-3 text-lg font-medium text-text-primary">System</h2>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <MetricCard
            label="Uptime"
            value={healthQuery.data ? formatUptime(healthQuery.data.uptime_seconds) : '--'}
            sub={
              healthQuery.data ? `${healthQuery.data.uptime_seconds.toLocaleString()}s` : undefined
            }
          />
          <MetricCard label="Version" value={healthQuery.data?.version ?? '--'} />
          <MetricCard
            label="Last Checked"
            value={lastChecked ? lastChecked.toLocaleTimeString() : '--'}
            sub={lastChecked ? lastChecked.toLocaleDateString() : undefined}
          />
        </div>
      </section>

      {/* Live indicator */}
      {healthQuery.data && (
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-green-500" />
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
          </span>
          <span className="text-xs font-medium text-green-600">Polling live</span>
        </div>
      )}
    </div>
  )
}
